import {
  Table,
  Card,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Tag,
  Typography,
  Button,
  message,
} from "antd";
import {
  SearchOutlined,
  CalendarOutlined,
  LockOutlined,
  UnlockOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "moment/locale/id";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import MyPage from "../../../../../components/pages/Page";
import { useFetch } from "../../../../../hooks/useFetch";
import { GetAPI, PostAPI } from "../../../../../redux";
import "../table-compact.css";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const columns = [
  { title: "No", dataIndex: "no", key: "no", width: 50, fixed: "left" },
  {
    title: "Nomor HAWB",
    dataIndex: "noHAWB",
    key: "noHAWB",
    fixed: "left",
    render: (text) => (
      <Text strong style={{ color: "#1890ff" }}>
        {text}
      </Text>
    ),
  },
  {
    title: "Status Barang",
    dataIndex: "statusBarang",
    key: "statusBarang",
    render: (status) => {
      const s = String(status ?? "");
      let color = "default";
      if (s === "SPPB") color = "success";
      else if (s === "Hold Customs") color = "error";
      else if (s === "SP2MP") color = "warning";
      else if (s === "SPJM") color = "processing";
      else if (s === "Gate Out") color = "green";
      else if (s === "Gate In") color = "gold";
      return (
        <Tag color={color} style={{ fontWeight: 500 }}>
          ● {s || "-"}
        </Tag>
      );
    },
  },
  {
    title: "Status Hold",
    dataIndex: "statusHold",
    key: "statusHold",
    render: (status) => {
      const s = String(status ?? "");
      if (!s) return "-";
      const t = s.toLowerCase();
      let color = "default";
      let label = s;
      if (t === "hold" || s === "Hold Customs") {
        color = "error";
        label = "Hold";
      } else if (t === "release" || s === "Released") {
        color = "cyan";
        label = "Release";
      } else if (s === "SPPB") {
        color = "success";
      }
      return (
        <Tag color={color} variant="filled">
          {label}
        </Tag>
      );
    },
  },
  { title: "Nomor Flight", dataIndex: "noFlight", key: "noFlight" },
  { title: "Nomor BC11", dataIndex: "noBC11", key: "noBC11" },
  { title: "Consignee", dataIndex: "consignee", key: "consignee" },
  { title: "Kode Dokumen In", dataIndex: "kodeDocIn", key: "kodeDocIn" },
  { title: "Nomor Dokumen In", dataIndex: "noDocIn", key: "noDocIn" },
  {
    title: "Tanggal Dokumen In",
    dataIndex: "tglDocIn",
    key: "tglDocIn",
    render: (text, record) => (
      <div>
        {record.jamDocIn}
        <br />
        <span style={{ color: "#bfbfbf" }}>{record.tglDocIn}</span>
      </div>
    ),
  },
  {
    title: "Tanggal Hold",
    dataIndex: "tglHold",
    key: "tglHold",
    render: (text, record) => (
      <div>
        {record.jamHold || "hh:mm:ss"}
        <br />
        <span style={{ color: "#bfbfbf" }}>
          {record.tglHold || "dd/mm/yyyy"}
        </span>
      </div>
    ),
  },
  {
    title: "Alasan Hold",
    dataIndex: "alasanHold",
    key: "alasanHold",
    render: (text) => text || "-",
  },
  {
    title: "Tanggal Release",
    dataIndex: "tglRelease",
    key: "tglRelease",
    render: (text, record) => (
      <div>
        {record.jamRelease || "hh:mm:ss"}
        <br />
        <span style={{ color: "#bfbfbf" }}>
          {record.tglRelease || "dd/mm/yyyy"}
        </span>
      </div>
    ),
  },
  {
    title: "Alasan Release",
    dataIndex: "alasanRelease",
    key: "alasanRelease",
    render: (text) => text || "-",
  },
  {
    title: "Lama Tegah",
    dataIndex: "lamaTegah",
    key: "lamaTegah",
    render: (text) =>
      text || <span style={{ color: "#8c8c8c" }}>hari:jam:menit</span>,
  },
  {
    title: "Nama Petugas BC (Hold)",
    dataIndex: "petugasHold",
    key: "petugasHold",
  },
  { title: "NIP Petugas BC", dataIndex: "nipPetugas", key: "nipPetugas" },
];

const PER_PAGE = 10;

const toYmd = (m) => (m ? m.format("YYYYMMDD") : "");

const buildQuery = (page = 1, range, perPage = PER_PAGE) => {
  const params = new URLSearchParams();
  const [start, end] = range || [];
  if (start) params.append("start_date", toYmd(start));
  if (end) params.append("end_date", toYmd(end));
  params.append("page", page);
  params.append("perPage", perPage);
  return `api/v1/beacukai/customshold?${params.toString()}`;
};

const parseDT = (v) => {
  if (!v && v !== 0) return null;
  const m = moment(
    v,
    ["YYYYMMDDHHmmss", "YYYYMMDD", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD", moment.ISO_8601],
    true
  );
  return m.isValid() ? m : null;
};

// respons API (payload.data): { data: [...], total, page, perPage, totalPage }
const mapRow = (r, i, page) => {
  const docInM = parseDT(r.tanggal_dok_in);
  const holdM = parseDT(r.tanggal_hold);
  const relM = parseDT(r.tanggal_release);
  return {
    key: r.no_hawb ? `${r.no_hawb}-${i}` : i,
    no: (page - 1) * PER_PAGE + i + 1,
    noHAWB: r.no_hawb ?? "-",
    statusBarang: r.status_barang ?? "-",
    statusHold: r.status_hold ?? "",
    noFlight: r.no_flight ?? "-",
    noBC11: r.no_bc11 ?? "-",
    consignee: r.consignee ?? "-",
    kodeDocIn: r.kode_dok_in ?? "-",
    noDocIn: r.nomor_dok_in ?? "-",
    jamDocIn: docInM ? docInM.format("HH:mm:ss") : "-",
    tglDocIn: docInM ? docInM.format("DD/MM/YYYY") : "-",
    jamHold: holdM ? holdM.format("HH:mm:ss") : "",
    tglHold: holdM ? holdM.format("DD/MM/YYYY") : "",
    alasanHold: r.alasan_hold ?? "-",
    jamRelease: relM ? relM.format("HH:mm:ss") : "",
    tglRelease: relM ? relM.format("DD/MM/YYYY") : "",
    alasanRelease: r.alasan_release ?? "-",
    lamaTegah: r.lama_tegah ?? "",
    petugasHold: r.nama_petugas_bc ?? "-",
    nipPetugas: r.nip_petugas_bc ?? "-",
  };
};

export default () => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [range, setRange] = useState([moment().subtract(6, "days"), moment()]);
  const [query, setQuery] = useState(() =>
    buildQuery(1, [moment().subtract(6, "days"), moment()])
  );
  const [rows, loading] = useFetch(query);
  const [lastUpdated, setLastUpdated] = useState(() => moment());

  // form Hold Customs (semua wajib)
  const [holdMhawb, setHoldMhawb] = useState("");
  const [holdName, setHoldName] = useState("");
  const [holdNip, setHoldNip] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [holdSubmitting, setHoldSubmitting] = useState(false);

  // lookup detail hold by mhawb (debounce, min. 5 karakter)
  // ada -> { date_hold }, tidak ada -> null
  const [holdInfo, setHoldInfo] = useState(null);

  const fetchHoldInfo = (mhawb) => {
    dispatch(GetAPI({ url: `api/v1/beacukai/customshold/${mhawb}` })).then(
      (resp) => {
        setHoldInfo(resp?.payload?.data || null);
      }
    );
  };

  useEffect(() => {
    const mhawb = holdMhawb.trim();
    if (mhawb.length <= 4) {
      setHoldInfo(null);
      return;
    }
    const t = setTimeout(() => fetchHoldInfo(mhawb), 600);
    return () => clearTimeout(t);
  }, [holdMhawb, dispatch]);

  // form Release Customs (mhawb + alasan wajib)
  const [releaseReason, setReleaseReason] = useState("");
  const [releaseSubmitting, setReleaseSubmitting] = useState(false);

  const handleRelease = () => {
    const mhawb = holdMhawb.trim();
    const missing = [];
    if (!mhawb) missing.push("Nomor M/HAWB");
    if (!releaseReason.trim()) missing.push("Alasan Lepas Tindak / Tegah");
    if (missing.length) {
      message.warning(`Wajib diisi: ${missing.join(", ")}`);
      return;
    }
    setReleaseSubmitting(true);
    dispatch(
      PostAPI({
        url: "api/v1/beacukai/customshold/release",
        data: { mhawb, reason_release: releaseReason.trim() },
      })
    ).then((resp) => {
      setReleaseSubmitting(false);
      if (resp?.payload) {
        message.success("Release Customs berhasil disimpan");
        setReleaseReason("");
        fetchHoldInfo(mhawb);
        setPage(1);
        setQuery(`${buildQuery(1, range)}&t=${Date.now()}`);
      }
    });
  };
  const [nowTick, setNowTick] = useState(() => moment());
  useEffect(() => {
    if (!holdInfo?.date_hold) return;
    setNowTick(moment());
    const t = setInterval(() => setNowTick(moment()), 30000);
    return () => clearInterval(t);
  }, [holdInfo?.date_hold]);

  const holdDateM = holdInfo?.date_hold ? moment(holdInfo.date_hold) : null;
  const holdElapsed = (() => {
    if (!holdDateM || !holdDateM.isValid()) return "";
    const diff = nowTick.diff(holdDateM);
    if (diff < 0) return "0:00:00";
    const dur = moment.duration(diff);
    const p = (n) => String(n).padStart(2, "0");
    return `${Math.floor(dur.asDays())}:${p(dur.hours())}:${p(dur.minutes())}`;
  })();

  useEffect(() => {
    if (rows) setLastUpdated(moment());
  }, [rows]);

  const handleTableChange = (pagination) => {
    const nextPage = pagination.current;
    setPage(nextPage);
    setQuery(buildQuery(nextPage, range));
  };

  const onRangeChange = (dates) => {
    setRange(dates);
    if (dates?.[0] && dates?.[1]) {
      setPage(1);
      setQuery(buildQuery(1, dates));
    }
  };

  const handleHold = () => {
    const missing = [];
    if (!holdMhawb.trim()) missing.push("Nomor M/HAWB");
    if (!holdName.trim()) missing.push("Nama Petugas BC");
    if (!holdNip.trim()) missing.push("NIP Petugas BC");
    if (!holdReason.trim()) missing.push("Alasan Penindakan / Penegahan");
    if (missing.length) {
      message.warning(`Wajib diisi: ${missing.join(", ")}`);
      return;
    }
    setHoldSubmitting(true);
    dispatch(
      PostAPI({
        url: "api/v1/beacukai/customshold/hold",
        data: {
          name_custom_hold: holdName.trim(),
          nip_custom_hold: holdNip.trim(),
          reason_hold: holdReason.trim(),
          mhawb: holdMhawb.trim(),
        },
      })
    ).then((resp) => {
      setHoldSubmitting(false);
      if (resp?.payload) {
        message.success("Hold Customs berhasil disimpan");
        setHoldMhawb("");
        setHoldName("");
        setHoldNip("");
        setHoldReason("");
        // refresh tabel & kembali ke halaman 1 agar data hold terbaru terlihat
        setPage(1);
        setQuery(`${buildQuery(1, range)}&t=${Date.now()}`);
      }
    });
  };

  const apiRows = Array.isArray(rows?.data) ? rows.data : [];
  const total = rows?.total ?? 0;
  const dataSource = apiRows.map((r, i) => mapRow(r, i, page));

  return (
    <MyPage title={"Customs Hold"}>
      <div
        className="dashboard-filters"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "20px",
          paddingBottom: "4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
            Filter Tampilan:
          </span>

          <Select
            defaultValue="harian"
            style={{ width: 100 }}
            size="small"
            options={[
              { value: "harian", label: "Harian" },
              { value: "mingguan", label: "Mingguan" },
            ]}
          />

          <Select
            defaultValue="bulanan"
            style={{ width: 105 }}
            size="small"
            options={[
              { value: "bulanan", label: "Bulanan" },
              { value: "tahunan", label: "Tahunan" },
            ]}
          />

          <RangePicker
            size="small"
            style={{ width: 300 }}
            prefixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            suffixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            separator=" - "
            value={range}
            onChange={onRangeChange}
            format="DD MMMM YYYY"
          />
        </div>

        <div style={{ fontSize: "12px", color: "#475569" }}>
          Terakhir Diperbarui:{" "}
          <span style={{ fontWeight: 500 }}>
            {lastUpdated.clone().locale("id").format("DD MMMM YYYY, HH:mm [WIB]")}
          </span>
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <span style={{ color: "#820014", fontWeight: 600 }}>
                <FileTextOutlined /> Tindakan Penegahan (Hold Customs)
              </span>
            }
            style={{ borderTop: "4px solid #cf1322", height: "100%" }}
          >
            <Row gutter={[12, 12]}>
              <Col span={8}>
                <Text
                  size="small"
                  type="secondary"
                  block
                  style={{ fontSize: 11, marginBottom: 4 }}
                >
                  Nomor M/HAWB (Hold by Customs)<span style={{ color: "red" }}> *</span>
                </Text>
                <Input
                  placeholder="Scan/Input"
                  size="small"
                  suffix={<SearchOutlined />}
                  value={holdMhawb}
                  onChange={(e) => setHoldMhawb(e.target.value)}
                />
              </Col>
              <Col span={8}>
                <Text
                  type="secondary"
                  block
                  style={{ fontSize: 11, marginBottom: 4 }}
                >
                  Nama Petugas BC<span style={{ color: "red" }}> *</span>
                </Text>
                <Input
                  size="small"
                  value={holdName}
                  onChange={(e) => setHoldName(e.target.value)}
                />
              </Col>
              <Col span={8}>
                <Text
                  type="secondary"
                  block
                  style={{ fontSize: 11, marginBottom: 4 }}
                >
                  NIP Petugas BC<span style={{ color: "red" }}> *</span>
                </Text>
                <Input
                  size="small"
                  value={holdNip}
                  onChange={(e) => setHoldNip(e.target.value)}
                />
              </Col>
              <Col span={24}>
                <Text
                  type="secondary"
                  block
                  style={{ fontSize: 11, marginBottom: 4 }}
                >
                  Alasan Penindakan / Penegahan<span style={{ color: "red" }}> *</span>
                </Text>
                <TextArea
                  rows={2}
                  size="small"
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                />
              </Col>
              <Col span={24} style={{ marginTop: 8 }}>
                <Button
                  type="primary"
                  danger
                  icon={<LockOutlined />}
                  block
                  loading={holdSubmitting}
                  onClick={handleHold}
                  style={{ backgroundColor: "#cf1322", fontWeight: 600 }}
                >
                  Hold
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <span style={{ color: "#389e0d", fontWeight: 600 }}>
                <FileTextOutlined /> Pelepasan Tegahan (Release Customs)
              </span>
            }
            style={{ borderTop: "4px solid #389e0d", height: "100%" }}
          >
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <Text
                  type="secondary"
                  block
                  style={{ fontSize: 11, marginBottom: 4 }}
                >
                  Status Waktu Hold
                </Text>
                <Input
                  placeholder="hh:mm:ss dd/mm/yyyy"
                  size="small"
                  readOnly
                  value={
                    holdDateM && holdDateM.isValid()
                      ? holdDateM.format("HH:mm:ss DD/MM/YYYY")
                      : ""
                  }
                />
              </Col>
              <Col span={12}>
                <Text
                  type="secondary"
                  block
                  style={{ fontSize: 11, marginBottom: 4 }}
                >
                  Waktu Terakhir Hold (Log)
                </Text>
                <Input
                  size="small"
                  disabled
                  value={
                    holdDateM && holdDateM.isValid()
                      ? holdDateM.format("HH:mm:ss DD/MM/YYYY")
                      : ""
                  }
                />
              </Col>
              <Col span={12}>
                <Text
                  type="secondary"
                  block
                  style={{ fontSize: 11, marginBottom: 4 }}
                >
                  Alasan Lepas Tindak / Tegah<span style={{ color: "red" }}> *</span>
                </Text>
                <TextArea
                  rows={2}
                  size="small"
                  value={releaseReason}
                  onChange={(e) => setReleaseReason(e.target.value)}
                />
              </Col>
              <Col span={12}>
                <Text
                  type="secondary"
                  block
                  style={{ fontSize: 11, marginBottom: 4 }}
                >
                  Status Waktu Release Lama Waktu Tegah
                </Text>
                <Input
                  placeholder="hari:jam:menit"
                  size="small"
                  disabled
                  value={holdElapsed}
                />
              </Col>
              <Col span={24} style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  icon={<UnlockOutlined />}
                  block
                  loading={releaseSubmitting}
                  onClick={handleRelease}
                  style={{
                    backgroundColor: "#389e0d",
                    borderColor: "#389e0d",
                    fontWeight: 600,
                  }}
                >
                  Release
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title={
          <span style={{ fontSize: 13, color: "#595959", fontWeight: 600 }}>
            📄 Rincian Data Current Now & Pergerakan Barang
          </span>
        }
        styles={{ body: { padding: 0 } }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
        bordered={false}
      >
        <Table
          className="bv2-compact"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            current: page,
            pageSize: PER_PAGE,
            total,
            showSizeChanger: false,
            showTotal: (t) => `Total ${t} data`,
          }}
          onChange={handleTableChange}
          size="small"
          scroll={{ x: 2000, y: 400 }}
          bordered
        />
      </Card>
    </MyPage>
  );
};
